// MediaPipe Hand Landmarker wrapper.
//
// Runs alongside the PoseDetector during calibration. The pose model
// gives us wrist positions but only a very coarse read on individual
// finger joints; the hand model gives us the full 21-landmark hand
// skeleton per hand, which we use to:
//
//   1. Compute a "hand span" in pixels (distance from thumb tip to
//      pinky tip on an open hand, or from wrist to middle finger tip
//      on a closed hand). Combined with a population-average hand
//      length (~18.5 cm adult), this yields a pixels-per-cm scale
//      that lets the CdA pipeline report absolute drag numbers
//      instead of relative-only ones.
//
//   2. Verify the rider's hands are actually on the bars during the
//      riding-pose lock — a low hand-detection score suggests the
//      pose landmarks put the wrists somewhere the model can't see
//      hands (e.g. mid-shift), so the lock probably isn't stable
//      yet.
//
// Loaded from the same MediaPipe WASM runtime + CDN as PoseDetector,
// so init cost is amortised. Uses hand_landmarker_lite (~2 MB) for
// snappy per-frame detection at 15-30 fps.

import {
  FilesetResolver,
  HandLandmarker,
  HandLandmarkerResult,
} from '@mediapipe/tasks-vision';

const WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/latest/hand_landmarker.task';

export interface HandLandmark {
  x: number;
  y: number;
  z: number;
}

/** MediaPipe hand skeleton — indices used later.
 *  0  WRIST
 *  4  THUMB_TIP
 *  8  INDEX_FINGER_TIP
 *  12 MIDDLE_FINGER_TIP
 *  16 RING_FINGER_TIP
 *  20 PINKY_TIP
 *  5  INDEX_FINGER_MCP  (knuckle)
 *  17 PINKY_MCP
 */
export const HAND_LM = {
  WRIST: 0,
  THUMB_TIP: 4,
  INDEX_TIP: 8,
  MIDDLE_TIP: 12,
  PINKY_TIP: 20,
  INDEX_MCP: 5,
  PINKY_MCP: 17,
};

export interface DetectedHand {
  landmarks: HandLandmark[]; // 21 entries
  handedness: 'Left' | 'Right' | 'Unknown';
  score: number; // 0..1
}

export interface HandDetectionResult {
  hands: DetectedHand[];
}

export class HandDetector {
  private landmarker: HandLandmarker | null = null;
  private initializing: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.landmarker) return;
    if (this.initializing) return this.initializing;
    this.initializing = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
      this.landmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numHands: 2,
        minHandDetectionConfidence: 0.5,
        minHandPresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
    })();
    await this.initializing;
  }

  detect(video: HTMLVideoElement, timestampMs: number): HandDetectionResult | null {
    if (!this.landmarker) return null;
    const result: HandLandmarkerResult = this.landmarker.detectForVideo(
      video,
      timestampMs,
    );
    const hands: DetectedHand[] = [];
    if (result.landmarks) {
      for (let i = 0; i < result.landmarks.length; i++) {
        const lm = result.landmarks[i];
        const cat = result.handedness?.[i]?.[0];
        hands.push({
          landmarks: lm.map((p) => ({ x: p.x, y: p.y, z: p.z })),
          handedness: (cat?.categoryName as 'Left' | 'Right') ?? 'Unknown',
          score: cat?.score ?? 0,
        });
      }
    }
    return { hands };
  }

  close(): void {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
    }
  }
}

// ---------------------------------------------------------------------------
// Derived measurements
// ---------------------------------------------------------------------------

/** Pixel distance from thumb tip to pinky tip — a proxy for hand
 *  span. Returns null if the required landmarks aren't present. */
export function handSpanPx(
  hand: DetectedHand,
  imgW: number,
  imgH: number,
): number | null {
  const thumb = hand.landmarks[HAND_LM.THUMB_TIP];
  const pinky = hand.landmarks[HAND_LM.PINKY_TIP];
  if (!thumb || !pinky) return null;
  const dx = (thumb.x - pinky.x) * imgW;
  const dy = (thumb.y - pinky.y) * imgH;
  return Math.hypot(dx, dy);
}

/** Pixel distance from wrist to middle-finger tip — a proxy for hand
 *  length. More robust than span when the hand is gripping bars (span
 *  collapses to a fist width but length is preserved). Returns null
 *  when landmarks are missing. */
export function handLengthPx(
  hand: DetectedHand,
  imgW: number,
  imgH: number,
): number | null {
  const wrist = hand.landmarks[HAND_LM.WRIST];
  const mid = hand.landmarks[HAND_LM.MIDDLE_TIP];
  if (!wrist || !mid) return null;
  const dx = (wrist.x - mid.x) * imgW;
  const dy = (wrist.y - mid.y) * imgH;
  return Math.hypot(dx, dy);
}

/** Convert a pixel measurement to centimetres using a reference hand
 *  length (population avg ~18.5 cm adult, tweakable per rider). */
export const AVG_HAND_LENGTH_CM = 18.5;
export function pxPerCmFromHand(
  hand: DetectedHand,
  imgW: number,
  imgH: number,
  refCm = AVG_HAND_LENGTH_CM,
): number | null {
  const lenPx = handLengthPx(hand, imgW, imgH);
  if (lenPx == null || lenPx < 5) return null;
  return lenPx / refCm;
}
